import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Pencil } from "lucide-react";
import { StaffForm } from "@/pages/Staff";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800 text-right">{value || "—"}</span>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="font-heading font-bold text-slate-900 mb-2">{title}</div>
    {children}
  </div>
);

export default function StaffProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => api.get(`/staff/${id}`).then((r) => setS(r.data)).catch(() => setS(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (s === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;
  if (s === false) return <div className="py-20 text-center text-slate-400">Staff not found.</div>;

  const canEdit = hasPerm(user, "edit_staff") && s.role !== "admin";

  return (
    <div className="space-y-5" data-testid="staff-profile">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => nav("/staff")} className="gap-1.5" data-testid="back-btn"><ArrowLeft size={16} />Back to Staff</Button>
        {canEdit && <Button onClick={() => setEditOpen(true)} className="gap-1.5" data-testid="profile-edit-btn"><Pencil size={16} />Edit Details</Button>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-5">
        <div className="w-24 h-24 rounded-xl bg-indigo-600 text-white overflow-hidden flex items-center justify-center text-2xl font-bold shrink-0">
          {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
        </div>
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">{s.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-indigo-100 text-indigo-700">{s.role}</span>
            {s.designation && <span className="text-sm text-slate-500">{s.designation}</span>}
            {s.staff_id && <span className="text-xs text-slate-400">ID: {s.staff_id}</span>}
            <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "Disabled" ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-700"}`}>{s.status || "Active"}</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Card title="Personal Details">
          <Row label="Father's Name" value={s.father_name} />
          <Row label="Gender" value={s.gender} />
          <Row label="Date of Birth" value={fmtDate(s.dob)} />
          <Row label="Marital Status" value={s.marital_status} />
          <Row label="Spouse Name" value={s.spouse_name} />
          <Row label="Nationality" value={s.nationality} />
          <Row label="Caste" value={s.caste} />
          <Row label="Religion" value={s.religion} />
          <Row label="Category" value={s.category} />
          <Row label="Aadhar No." value={s.aadhar_number} />
        </Card>

        <Card title="Contact & Professional">
          <Row label="Phone" value={s.phone} />
          <Row label="Email" value={s.email} />
          <Row label="Address" value={s.address} />
          <Row label="Qualification" value={s.qualification} />
          <Row label="Joining Date" value={fmtDate(s.joining_date)} />
          <Row label="Monthly Salary" value={s.salary ? fmt(s.salary) : "—"} />
          <Row label="Last Organisation" value={s.last_organisation} />
          <Row label="Last Position" value={s.last_position} />
          <Row label="Experience" value={s.experience} />
        </Card>

        <Card title="Bank Details">
          <Row label="Account Holder Name" value={s.account_holder_name} />
          <Row label="Account Number" value={s.account_number} />
          <Row label="IFSC Code" value={s.ifsc_code} />
          <Row label="Bank Name" value={s.bank_name} />
          <Row label="Branch Name" value={s.branch_name} />
          <Row label="PAN No." value={s.pan_no} />
          <Row label="PF Account Number" value={s.pf_account_number} />
        </Card>

        <Card title="Documents">
          <div className="grid grid-cols-2 gap-4">
            {[["Profile Photo", s.photo], ["Aadhar Photo", s.aadhar_photo]].map(([label, src]) => (
              <div key={label}>
                <div className="text-xs text-slate-500 mb-1">{label}</div>
                {src ? <img src={src} alt={label} className="w-full h-40 object-cover rounded-lg border border-slate-200" /> :
                  <div className="w-full h-40 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-300">Not uploaded</div>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <StaffForm open={editOpen} onOpenChange={setEditOpen} edit={s} onSaved={load} />
    </div>
  );
}
